import type { ReactNode } from "react";
import { useState } from "react";
import { useParams, Link } from "wouter";
import { trpc } from "../lib/trpc";
import { Card, Badge, Spinner, ProductImage } from "../components/ui";
import { fmtEur, fmtNum, fmtDateTime, fmtDateShort } from "../lib/format";

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div>
      <div className="text-xs font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 text-sm font-medium text-slate-800">{value || <span className="text-slate-300">—</span>}</div>
    </div>
  );
}

/** Small clipboard button with a brief "copied" confirmation. */
function CopyButton({ text, title = "Copy" }: { text: string; title?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      onClick={() => { void navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}
      className={`inline-flex items-center rounded px-1 text-xs ${copied ? "text-green-600" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}
      title={copied ? "Copied!" : title}
    >
      {copied ? "✓" : "⧉"}
    </button>
  );
}

function StatBox({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl bg-slate-50 p-3 text-center">
      <div className="text-xs uppercase tracking-wide text-slate-400">{label}</div>
      <div className="mt-0.5 text-xl font-semibold text-slate-900">{value}</div>
    </div>
  );
}

function StockChart({ points }: { points: { recordedAt: string | Date; stockValue: number | null }[] }) {
  if (points.length < 2) return <div className="py-6 text-center text-sm text-slate-400">Not enough data to chart.</div>;
  const vals = points.map((p) => p.stockValue ?? 0);
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  const W = 820, H = 240, pad = 30;
  const range = max - min || 1;
  const x = (i: number) => pad + (i / (points.length - 1)) * (W - 2 * pad);
  const y = (v: number) => H - pad - ((v - min) / range) * (H - 2 * pad);
  const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p.stockValue ?? 0).toFixed(1)}`).join(" ");
  const area = `${line} L${x(points.length - 1).toFixed(1)},${H - pad} L${x(0).toFixed(1)},${H - pad} Z`;
  const fmtD = (d: string | Date) => fmtDateShort(d); // Amsterdam time, like every other stamp in the app
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full" style={{ maxHeight: 260 }}>
      {[max, Math.round((max + min) / 2), min].map((v, i) => (
        <g key={i}>
          <line x1={pad} x2={W - pad} y1={y(v)} y2={y(v)} stroke="#eef2f7" />
          <text x={4} y={y(v) + 4} fontSize="11" fill="#94a3b8">{v}</text>
        </g>
      ))}
      <path d={area} fill="#6366f1" fillOpacity={0.12} />
      <path d={line} fill="none" stroke="#6366f1" strokeWidth={2} />
      {points.map((p, i) => <circle key={i} cx={x(i)} cy={y(p.stockValue ?? 0)} r={3} fill="#6366f1" />)}
      {points.map((p, i) => (i === 0 || i === points.length - 1 || i === Math.floor(points.length / 2) ? (
        <text key={`d${i}`} x={x(i)} y={H - 8} fontSize="11" fill="#94a3b8" textAnchor="middle">{fmtD(p.recordedAt)}</text>
      ) : null))}
    </svg>
  );
}

/* ── Per-product change history ──────────────────────────────────────────────────────────────── */

const asObj = (v: unknown): Record<string, unknown> => {
  if (v && typeof v === "object") return v as Record<string, unknown>;
  if (typeof v === "string") { try { return JSON.parse(v) as Record<string, unknown>; } catch { return {}; } }
  return {};
};
const money = (n: unknown) => (typeof n === "number" ? `€${n.toFixed(2)}` : String(n ?? "—"));

/** Summary of one field change (from → to). Shared by single-field ops and coalesced-op entries. */
function oneFieldSummary(c: { field?: string; from?: unknown; to?: unknown; deltaPct?: number | null; reason?: string; tracking?: string }): string {
  const money = (n: unknown) => (typeof n === "number" ? `€${n.toFixed(2)}` : String(n ?? "—"));
  const ft = (label: string) => `${label}: ${String(c.from ?? "—")} → ${String(c.to ?? "—")}`;
  switch (c.field) {
    case "visibility": return `visibility → ${String(c.to)}${c.reason ? ` (${c.reason})` : ""}`;
    case "price": return `price ${money(c.from)} → ${money(c.to)}${c.deltaPct != null ? ` (${c.deltaPct > 0 ? "+" : ""}${c.deltaPct}%)` : ""}`;
    case "purchase": return `purchase ${money(c.from)} → ${money(c.to)}`;
    case "stock": return `stock ${String(c.from ?? 0)} → ${String(c.to ?? 0)}${c.tracking === "enabled" ? " (track)" : ""}`;
    case "weight": return `weight ${String(c.from ?? 0)}g → ${String(c.to ?? 0)}g`;
    case "ean": return ft("EAN");
    case "supplier": return ft("supplier");
    case "name": return ft("name");
    case "brand": return ft("brand");
    case "depositNotice": return `deposit notice ${String(c.from ?? "—")} → ${String(c.to ?? "—")}`;
    case "category": return `category + ${String(c.to ?? "")}`;
    default: return c.field ? ft(c.field) : "";
  }
}

/** One-line "what changed, from → to" from an op diff — covers every field the reconcile emits. */
function summarizeChange(diff: unknown, opType?: string | null): string {
  const d = asObj(diff) as {
    kind?: string; action?: string; fields?: string[]; field?: string; from?: unknown; to?: unknown;
    deltaPct?: number; sales?: { from: number; to: number }; purchase?: { from: number; to: number };
    itemCode?: string; reason?: string; tracking?: string;
    changes?: { field?: string; from?: unknown; to?: unknown; deltaPct?: number | null; reason?: string; tracking?: string }[];
  };
  if (opType === "create" || d.kind === "create") return `create${d.itemCode ? ` ${d.itemCode}` : ""}`;
  // Coalesced Lightspeed op — summarize each field change.
  if (Array.isArray(d.changes)) return d.changes.map(oneFieldSummary).filter(Boolean).join(" · ") || "—";
  if (d.field) return oneFieldSummary(d);
  const parts: string[] = [];
  if (d.action) parts.push(d.action);
  if (d.sales) parts.push(`sales ${money(d.sales.from)}→${money(d.sales.to)}`);
  if (d.purchase) parts.push(`purchase ${money(d.purchase.from)}→${money(d.purchase.to)}`);
  const labels: Record<string, string> = { Description: "description", SupplierItemCode: "supplier code" };
  const others = (d.fields ?? []).filter((f) => !["EndDate", "Status", "SalesPrice", "PurchasePrice", "StandardSalesPrice"].includes(f)).map((f) => labels[f] ?? f);
  if (others.length) parts.push(`sync ${others.join(", ")}`);
  return parts.join(" · ") || "—";
}

function histStatusBadge(status?: string | null) {
  if (!status) return null;
  const color = status === "applied" ? "green" : status === "failed" ? "red" : status === "skipped" ? "gray" : status === "dry_run" ? "blue" : "amber";
  return <Badge color={color}>{status}</Badge>;
}

function ChangeHistory({ code }: { code: string }) {
  const [target, setTarget] = useState<"all" | "exact" | "lightspeed">("all");
  const [status, setStatus] = useState<"all" | "pending" | "approved" | "applied" | "failed" | "skipped" | "dry_run">("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const q = trpc.pbs.changeHistory.useQuery({ productCode: code, target, status, from: from || undefined, to: to || undefined });
  const rows = q.data ?? [];
  const selCls = "rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700";
  const dateActive = !!(from || to);
  return (
    <Card title={`🕓 Change history${rows.length ? ` (${rows.length})` : ""}`}>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <select value={target} onChange={(e) => setTarget(e.target.value as typeof target)} className={selCls}>
          <option value="all">all systems</option>
          <option value="exact">ExactOnline</option>
          <option value="lightspeed">Lightspeed</option>
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className={selCls}>
          <option value="all">all statuses</option>
          <option value="applied">applied</option>
          <option value="pending">pending</option>
          <option value="approved">approved</option>
          <option value="failed">failed</option>
          <option value="skipped">skipped</option>
          <option value="dry_run">dry-run</option>
        </select>
        <label className="flex items-center gap-1 text-xs text-slate-500">from
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={`${selCls} ${from ? "border-amber-400" : ""}`} />
        </label>
        <label className="flex items-center gap-1 text-xs text-slate-500">to
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={`${selCls} ${to ? "border-amber-400" : ""}`} />
        </label>
        {dateActive && <button onClick={() => { setFrom(""); setTo(""); }} className="text-xs text-indigo-600 hover:underline">clear dates</button>}
      </div>
      {q.isLoading ? <Spinner /> : rows.length === 0 ? (
        <p className="py-4 text-center text-sm text-slate-400">No changes recorded for this product yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                <th className="py-2 pr-3 font-medium">When</th>
                <th className="py-2 pr-3 font-medium">System</th>
                <th className="py-2 pr-3 font-medium">What changed</th>
                <th className="py-2 pr-3 font-medium">Result</th>
                <th className="py-2 pr-3 font-medium">Set</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.opId} className="border-b border-slate-50 align-top">
                  <td className="whitespace-nowrap py-2 pr-3 text-xs text-slate-500">{fmtDateTime(r.appliedAt ?? r.csCreatedAt)}</td>
                  <td className="py-2 pr-3"><Badge color={r.target === "lightspeed" ? "blue" : "gray"}>{r.target === "lightspeed" ? "Lightspeed" : "ExactOnline"}</Badge></td>
                  <td className="py-2 pr-3 text-slate-700">
                    {summarizeChange(r.diff, r.opType)}
                    {r.lastError && <div className="mt-0.5 text-xs text-red-600">{r.lastError}</div>}
                  </td>
                  <td className="py-2 pr-3">{histStatusBadge(r.status)}</td>
                  <td className="py-2 pr-3 text-xs">
                    <Link href={`/reconcile/${r.changeSetId}`} className="font-mono text-indigo-600 hover:underline">#{r.changeSetId}</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

/* ── All PBS detail (dimensions, attributes, packaging units, codes) ─────────────────────────────── */

interface PbsInfoUnit { unit: string; quantityInUnit: number; weight: number; length: number; width: number; height: number; ean: string }
interface PbsDetailsRaw {
  ecologic?: string; intrastatCode?: string; productGroup?: string; pageInCatalog?: string; lifeCycle?: string;
  searchStringNL?: string; brandImage?: string; categoryNL?: string; subCategoryNL?: string;
  dimensions?: { weight: number; length: number; width: number; height: number } | null;
  attributes?: { field: string; value: string }[];
  infoUnits?: PbsInfoUnit[];
  costs?: { description: string; packSizeCosts: number; price: number }[];
}

/** Deposit (statiegeld) and statutory levies (Stibat, Thuiskopie) from the PBS <costs> block. */
function DepositCosts({ costs }: { costs: { description: string; packSizeCosts: number; price: number }[] }) {
  if (costs.length === 0) return null;
  const fmtP = (n: number) => `€ ${n.toFixed(3).replace(/0$/, "")}`;
  return (
    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3">
      <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">♻ Deposit & statutory levies (from PBS)</div>
      <div className="mt-2 space-y-1 text-sm">
        {costs.map((c, i) => {
          const qty = c.packSizeCosts || 1;
          return (
            <div key={i} className="flex flex-wrap items-baseline justify-between gap-x-4">
              <span className="text-slate-700">{c.description}</span>
              <span className="font-medium text-slate-900">{qty} × {fmtP(c.price)} = {fmtP(qty * c.price)}</span>
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 text-[11px] text-amber-700/80">Included in the posted purchase/sales prices above (deposit products also carry a "Prijs inclusief … statiegeld" notice in the webshop text).</div>
    </div>
  );
}

function PbsAllDetails({ code }: { code: string }) {
  const q = trpc.pbs.productDetails.useQuery({ productCode: code });
  if (q.isLoading) return <Card title="🧾 All PBS data"><Spinner /></Card>;
  // The section is ALWAYS shown. `d` is null when no detail row was imported for this product yet — in
  // that case every field reads "Unknown" and a banner explains why. When a row exists but a specific
  // field is empty in the supplier data, that field reads "—". So you can always tell: has a value /
  // known-but-empty / not imported.
  const d = q.data;
  const imported = !!d;
  const raw = (d?.raw ?? {}) as PbsDetailsRaw;
  const unk = (v: string | number | null | undefined, suffix = "") =>
    !imported ? "Unknown" : v != null && String(v).trim() !== "" && v !== 0 ? `${v}${suffix}` : "—";
  const attrs = (raw.attributes ?? []).filter((a) => a.field || a.value);
  const units = raw.infoUnits ?? [];
  const codes: [string, string | undefined][] = [
    ["Product group", raw.productGroup], ["Intrastat code", raw.intrastatCode], ["Life cycle", raw.lifeCycle],
    ["Page in catalog", raw.pageInCatalog], ["PBS category", raw.categoryNL], ["PBS subcategory", raw.subCategoryNL],
  ];
  const label = "text-xs font-medium uppercase tracking-wide text-slate-400";
  return (
    <Card title="🧾 All PBS data">
      {!imported && (
        <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Not imported for this product yet — run a PBS sync (Data Sync) to fill these in. Fields read
          "Unknown" until then. (If they stay empty after a sync, this product isn't in the PBS product feed.)
        </p>
      )}

      {/* Dimensions + eco — always shown */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
        <StatBox label="Weight" value={unk(d?.weight, " g")} />
        <StatBox label="Length" value={unk(d?.length, " mm")} />
        <StatBox label="Width" value={unk(d?.width, " mm")} />
        <StatBox label="Height" value={unk(d?.height, " mm")} />
        <StatBox label="Ecologic" value={imported ? (d!.ecologic ? "Yes ♻" : "No") : "Unknown"} />
      </div>

      {/* Attributes — always shown */}
      <div className="mb-4">
        <div className={`mb-1 ${label}`}>Attributes</div>
        {attrs.length > 0 ? (
          <div className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2">
            {attrs.map((a, i) => (
              <div key={i} className="flex justify-between border-b border-slate-50 py-1 text-sm">
                <span className="text-slate-500">{a.field}</span>
                <span className="font-medium text-slate-800">{a.value}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400">{imported ? "— (none provided)" : "Unknown"}</p>
        )}
      </div>

      {/* Codes — always shown, "—"/"Unknown" when empty */}
      <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {codes.map(([lab, v]) => <Field key={lab} label={lab} value={unk(v)} />)}
      </div>

      {/* Packaging units — always shown */}
      <div className="mb-4">
        <div className={`mb-1 ${label}`}>Packaging units</div>
        {units.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-100 text-left text-xs uppercase tracking-wide text-slate-400">
                  <th className="py-1.5 pr-3 font-medium">Unit</th>
                  <th className="py-1.5 pr-3 font-medium">Qty</th>
                  <th className="py-1.5 pr-3 font-medium">Weight</th>
                  <th className="py-1.5 pr-3 font-medium">L×W×H</th>
                  <th className="py-1.5 pr-3 font-medium">EAN</th>
                </tr>
              </thead>
              <tbody>
                {units.map((u, i) => (
                  <tr key={i} className="border-b border-slate-50">
                    <td className="py-1.5 pr-3 font-medium text-slate-800">{u.unit}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{u.quantityInUnit}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{u.weight > 0 ? `${u.weight} g` : "—"}</td>
                    <td className="py-1.5 pr-3 text-slate-600">{u.length || u.width || u.height ? `${u.length}×${u.width}×${u.height} mm` : "—"}</td>
                    <td className="py-1.5 pr-3 font-mono text-xs text-slate-600">{u.ean || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-sm text-slate-400">{imported ? "— (none provided)" : "Unknown"}</p>
        )}
      </div>

      {/* Search keywords — always shown */}
      <div>
        <div className={`mb-1 ${label}`}>Search keywords</div>
        <p className="text-xs leading-relaxed text-slate-500">{imported ? (raw.searchStringNL || "—") : "Unknown"}</p>
      </div>
    </Card>
  );
}

export default function ProductDetail() {
  const params = useParams();
  const code = params.code ?? "";
  const p = trpc.pbs.getByCode.useQuery({ productCode: code });
  const hist = trpc.pbs.stockHistory.useQuery({ productCode: code, limit: 60 });
  const catPath = trpc.pbs.categoryPath.useQuery({ categoryId: p.data?.categoryId ?? "" }, { enabled: !!p.data?.categoryId });
  const exact = trpc.pbs.exactLink.useQuery({ productCode: code }, { enabled: !!p.data });
  const details = trpc.pbs.productDetails.useQuery({ productCode: code }, { enabled: !!p.data });
  // Reads Lightspeed live, so it is deliberately not refetched on every focus change.
  const attrView = trpc.config.attrMap.productView.useQuery({ productCode: code }, { enabled: !!p.data, staleTime: 60_000, refetchOnWindowFocus: false });
  const [showExact, setShowExact] = useState(false);
  const [zoomImg, setZoomImg] = useState<string | null>(null);

  if (p.isLoading) return <Spinner label="Loading…" />;
  if (!p.data) return <p className="text-sm text-slate-500">Product not found. <Link href="/products" className="text-indigo-600">Back to products</Link></p>;
  const d = p.data;

  const points = (hist.data ?? []).slice().reverse().map((h) => ({ recordedAt: h.recordedAt, stockValue: h.stockValue }));
  const vals = points.map((x) => x.stockValue ?? 0);
  const current = vals.length ? vals[vals.length - 1]! : (d.stockValue ?? 0);
  const avg = vals.length ? Math.round(vals.reduce((a, b) => a + b, 0) / vals.length) : 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <Link href="/products" className="text-sm text-indigo-600">← Products</Link>
        <div className="mt-1 flex items-start gap-3">
          <ProductImage file={d.productImage} alt={d.descriptionNL ?? d.productCode} className="h-14 w-14 shrink-0 rounded-lg border border-slate-200" />
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-semibold text-slate-900">{d.descriptionNL || "No description"}</h1>
              {d.active ? <Badge color="green">Active</Badge> : <Badge color="gray">Inactive</Badge>}
            </div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-slate-500">
              <span className="font-mono">{d.productCode}</span>
              <span>·</span><span>{d.brand}</span>
              {d.eanCode && (
                <><span>·</span><span>EAN: <a className="text-indigo-600 underline" href={`https://www.barcodelookup.com/${d.eanCode}`} target="_blank" rel="noreferrer">{d.eanCode}</a><CopyButton text={d.eanCode} title="Copy EAN" /></span></>
              )}
              {exact.data?.itemCode && (
                <button onClick={() => setShowExact(true)} className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700 ring-1 ring-indigo-200 hover:bg-indigo-100" title="View the ExactOnline item stored in this app">
                  ItemCode: {exact.data.itemCode} ↗
                </button>
              )}
            </div>
            {catPath.data && catPath.data.length > 0 && (
              <nav className="mt-1.5 flex flex-wrap items-center gap-1 text-xs text-slate-500">
                <span className="text-slate-400">🗂</span>
                {catPath.data.map((c, i) => (
                  <span key={c.categoryId} className="flex items-center gap-1">
                    {i > 0 && <span className="text-slate-300">/</span>}
                    <span className={i === catPath.data!.length - 1 ? "rounded bg-amber-100 px-1.5 py-0.5 text-amber-800" : ""}>{c.name || c.categoryId}</span>
                  </span>
                ))}
              </nav>
            )}
          </div>
        </div>
      </div>

      {/* Images (main + extras from PBS — needed for Lightspeed) */}
      {(() => {
        const rawExtras = d.extraImages;
        const parsed = typeof rawExtras === "string" ? (() => { try { return JSON.parse(rawExtras); } catch { return []; } })() : rawExtras;
        const extras = (Array.isArray(parsed) ? parsed : []) as { type?: string; name?: string }[];
        const all = [{ type: "Main", name: d.productImage }, ...extras].filter((x) => x.name);
        if (all.length === 0) return null; // only hide when there's no picture at all
        return (
          <Card title={`🖼 Images (${all.length})`}>
            <div className="flex flex-wrap gap-3">
              {all.map((img) => (
                <button key={img.name} onClick={() => setZoomImg(img.name!)} className="group block w-28 text-left" title="Click to zoom">
                  <ProductImage file={img.name} alt={img.name ?? ""} className="h-28 w-28 cursor-zoom-in rounded-lg border border-slate-200 group-hover:border-indigo-400" />
                  <div className="mt-1 truncate text-center text-[11px] text-slate-500" title={img.name ?? ""}>
                    <span className="rounded bg-slate-100 px-1 text-[10px] text-slate-500">{img.type || "Extra"}</span> {img.name}
                  </div>
                </button>
              ))}
            </div>
          </Card>
        );
      })()}

      {/* Attributes: what PBS delivers, what our mapping makes of it, and what the shop carries now.
          Read live from Lightspeed on purpose — a cached copy would answer the wrong question. */}
      {(() => {
        const av = attrView.data;
        if (attrView.isLoading) return <Card title="🏷 Attributen"><Spinner label="Attributen laden…" /></Card>;
        if (!av || (!av.pbs.length && !av.onShop.length)) return null;
        const unmapped = av.pbs.filter((a) => !a.maps).length;
        const missingOnShop = av.pbs.filter((a) => a.maps && !av.onShop.some((s) => s.valueId === a.maps!.valueId));
        return (
          <Card
            title={`🏷 Attributen (${av.pbs.length} uit PBS · ${av.onShop.length} in Lightspeed)`}
            actions={
              <button
                onClick={() => attrView.refetch()}
                disabled={attrView.isFetching}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-200 disabled:opacity-50"
                title="Leest de webshop opnieuw — handig direct na een wijziging in Lightspeed"
              >
                {attrView.isFetching ? "Lezen…" : "opnieuw lezen"}
              </button>
            }
          >
            <div className="grid gap-5 lg:grid-cols-2">
              <div>
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">Uit PBS</div>
                {!av.pbs.length && <p className="text-xs text-slate-400">Geen attributen in de PBS-feed.</p>}
                <div className="max-h-72 overflow-auto">
                  {av.pbs.map((a) => (
                    <div key={`${a.field}|${a.value}`} className="flex items-baseline gap-2 border-b border-slate-50 py-1 text-xs last:border-0">
                      <span className="w-44 shrink-0 truncate text-slate-500" title={a.field}>{a.field}</span>
                      <span className="flex-1 truncate font-medium text-slate-700">{a.value}</span>
                      {a.maps
                        ? <span className="shrink-0 text-[11px] text-indigo-700" title={`Gekoppeld aan ${a.maps.filterTitle}`}>→ {a.maps.valueTitle}</span>
                        : <span className="shrink-0 text-[11px] text-slate-300">geen koppeling</span>}
                    </div>
                  ))}
                </div>
                {unmapped > 0 && (
                  <p className="mt-1 text-[11px] text-slate-500">
                    {unmapped} attribuut/attributen zonder filterkoppeling. Alle attributen hieronder komen onderaan het <b>INHOUD</b>-veld
                    in Lightspeed te staan; de korte <b>Beschrijving</b> blijft ongemoeid.
                  </p>
                )}
                {av.specBlock && (
                  <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-2">
                    <div className="mb-1 text-[11px] uppercase tracking-wide text-slate-400">komt onderaan bij INHOUD te staan</div>
                    <div className="text-xs text-slate-700 [&_h3]:mb-1 [&_h3]:font-semibold [&_li]:list-disc [&_ul]:ml-5" dangerouslySetInnerHTML={{ __html: av.specBlock }} />
                  </div>
                )}
              </div>

              <div>
                <div className="mb-1 text-xs font-medium uppercase tracking-wide text-slate-500">In Lightspeed</div>
                {!av.lsProductId && <p className="text-xs text-amber-700">Dit artikel staat niet op Lightspeed.</p>}
                {av.shopError && <p className="text-xs text-red-600">Kon de shop niet lezen: {av.shopError}</p>}
                {av.lsProductId && !av.shopError && !av.onShop.length && <p className="text-xs text-slate-400">Geen filterwaarden op dit product.</p>}
                <div className="max-h-72 overflow-auto">
                  {av.onShop.map((s) => (
                    <div key={s.valueId} className="flex items-baseline gap-2 border-b border-slate-50 py-1 text-xs last:border-0">
                      <span className="w-32 shrink-0 truncate text-slate-500">{s.filterTitle}</span>
                      <span className="flex-1 truncate font-medium text-slate-700">{s.valueTitle}</span>
                      {s.fromPbs
                        ? <span className="shrink-0 text-[11px] text-emerald-700" title="Komt overeen met een PBS-attribuut">✓ uit PBS</span>
                        : <span className="shrink-0 text-[11px] text-slate-400" title="Staat in de shop maar volgt niet uit PBS">handmatig</span>}
                    </div>
                  ))}
                </div>
                {missingOnShop.length > 0 && (
                  <p className="mt-1 text-[11px] text-amber-700">
                    {missingOnShop.length} gekoppelde waarde(n) staan nog niet op het product: {missingOnShop.map((a) => a.maps!.valueTitle).join(", ")}.
                  </p>
                )}
              </div>
            </div>
          </Card>
        );
      })()}

      {/* Pricing + Stock */}
      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="💲 Pricing">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Net price (per unit)" value={fmtEur(d.netPrice)} />
            <Field label="Tariff price" value={fmtEur(d.tarifPrice)} />
            {/* Computed here, not taken from PBS: PBS still applies the pre-2019 low rate of 6% to its
                own incl-VAT figure, so every 9% article arrives ~2,8% too low (733 of them, plus 545 at
                21%). Nothing downstream uses this field — the sync prices off the excl amount — but a
                wrong number on the page is a wrong number. PBS's own value is shown when it differs. */}
            {(() => {
              const excl = parseFloat(String(d.retailPriceExclVat ?? "0"));
              const rate = Number(d.vatRate ?? 0);
              const pbsValue = parseFloat(String(d.retailPriceInclVat ?? "0"));
              const computed = excl > 0 ? excl * (1 + rate / 100) : 0;
              const drifted = computed > 0 && pbsValue > 0 && Math.abs(pbsValue - computed) > 0.01;
              return (
                <Field
                  label="Retail incl. VAT"
                  value={
                    computed > 0 ? (
                      <>
                        {fmtEur(computed.toFixed(2))}
                        {drifted && (
                          <span className="ml-1 text-xs font-normal text-amber-600" title={`PBS levert dit veld met ${((pbsValue / excl - 1) * 100).toFixed(1)}% btw in plaats van ${rate}%`}>
                            (PBS: {fmtEur(d.retailPriceInclVat)})
                          </span>
                        )}
                      </>
                    ) : (
                      fmtEur(d.retailPriceInclVat)
                    )
                  }
                />
              );
            })()}
            <Field label="Retail excl. VAT" value={fmtEur(d.retailPriceExclVat)} />
            <Field label="Discount" value={d.discount ? `${parseFloat(d.discount).toFixed(2)}%` : "—"} />
            <Field label="VAT rate" value={`${d.vatRate}%`} />
          </div>
          {/* The PRICES ACTUALLY POSTED: PBS prices are per-piece; ExactOnline/Lightspeed sell the pack
              (STUK/pickSize), so purchase/sales = (per-piece price + deposit/levies) × pick — mirrors
              purchasePrice()/retailPrice() on the server (costs only on top of a real, non-zero price). */}
          {(() => {
            const pick = d.pickSize || 1;
            const costsList = ((details.data?.raw ?? {}) as PbsDetailsRaw).costs ?? [];
            const unitCosts = costsList.reduce((s, c) => s + (c.packSizeCosts || 1) * c.price, 0);
            const baseBuy = parseFloat(d.netPrice ?? "0");
            const baseSell = parseFloat(d.retailPriceExclVat ?? "0");
            const buy = baseBuy > 0 ? (baseBuy + unitCosts) * pick : 0;
            const sell = baseSell > 0 ? (baseSell + unitCosts) * pick : 0;
            return (
              <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-3">
                <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Posted to ExactOnline / Lightspeed · pack of {pick} (STUK/{pick})</div>
                <div className="mt-2 flex flex-wrap gap-x-10 gap-y-2 text-sm">
                  <div><div className="text-xs text-slate-500">Purchase price</div><div className="text-lg font-semibold text-slate-900">€ {buy.toFixed(2)}</div></div>
                  <div><div className="text-xs text-slate-500">Sales price (excl. VAT)</div><div className="text-lg font-semibold text-slate-900">€ {sell.toFixed(2)}</div></div>
                </div>
                {pick !== 1 && <div className="mt-1.5 text-[11px] text-indigo-700/70">= per-unit € {parseFloat(d.netPrice ?? "0").toFixed(2)} / € {parseFloat(d.retailPriceExclVat ?? "0").toFixed(2)} × pick size {pick}</div>}
                {unitCosts > 0 && <div className="mt-1.5 text-[11px] text-indigo-700/70">includes € {(unitCosts * pick).toFixed(2)} deposit/levies per pack (see below)</div>}
              </div>
            );
          })()}
          <DepositCosts costs={((details.data?.raw ?? {}) as PbsDetailsRaw).costs ?? []} />
        </Card>
        <Card title="📦 Stock & Packaging">
          <div className="mb-4 flex items-center gap-2">
            <span className="text-2xl font-semibold">{fmtNum(d.stockValue)}</span>
            {(d.stockValue ?? 0) > 0 ? <Badge color="green">In stock</Badge> : <Badge color="amber">No stock</Badge>}
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Pick size" value={d.pickSize || 1} />
            <Field label="Pack size" value={d.packSize || 1} />
            <Field label="Big pack size" value={d.bigPackSize || 1} />
            <Field label="Minimal size" value={d.minimalSize || 1} />
          </div>
        </Card>
      </div>

      {/* Product information */}
      <Card title="🏷 Product Information">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <Field label="Product code" value={<span className="font-mono">{d.productCode}</span>} />
          <Field label="Brand" value={d.brand} />
          <Field label="EAN code" value={d.eanCode ? <span className="inline-flex items-center gap-0.5"><a className="text-indigo-600 underline" href={`https://www.barcodelookup.com/${d.eanCode}`} target="_blank" rel="noreferrer">{d.eanCode}</a><CopyButton text={d.eanCode} title="Copy EAN" /></span> : null} />
          <Field label="Status" value={d.active ? "Active" : "Inactive"} />
          <Field label="Category (deepest)" value={catPath.data?.at(-1)?.name ?? d.categoryId} />
          <Field label="ExactOnline ItemCode" value={exact.data?.itemCode ?? "— (not in ExactOnline)"} />
          <Field label="Product image" value={d.productImage ? <a className="text-indigo-600" href={`/api/pbs-image/${encodeURIComponent(d.productImage)}`} target="_blank" rel="noreferrer">{d.productImage}</a> : "—"} />
          <Field label="Last updated" value={fmtDateTime(d.lastUpdated)} />
        </div>
      </Card>

      {/* Stock history */}
      <Card title={`📊 Stock History (${points.length} data points)`}>
        {hist.isLoading ? <Spinner /> : (
          <>
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <StatBox label="Current" value={fmtNum(current)} />
              <StatBox label="Average" value={fmtNum(avg)} />
              <StatBox label="Min" value={fmtNum(vals.length ? Math.min(...vals) : 0)} />
              <StatBox label="Max" value={fmtNum(vals.length ? Math.max(...vals) : 0)} />
            </div>
            <StockChart points={points} />
          </>
        )}
      </Card>

      {/* Descriptions */}
      <Card title="📄 Descriptions">
        <div className="space-y-3">
          <Field label="Standard description" value={d.descriptionNL} />
          <Field label="Invoice description" value={d.invoiceDescriptionNL} />
          <Field label="E-commerce description" value={d.ecommDescriptionNL} />
          <Field label="Product detail" value={d.productDetailNL} />
          <Field label="Commercial description" value={d.commDescriptionNL} />
        </div>
      </Card>

      {/* All PBS data (dimensions, attributes, packaging units, codes) — everything the feed carries */}
      <PbsAllDetails code={code} />

      {/* Transactional change history (what changed on ExactOnline / Lightspeed, from → to) */}
      <ChangeHistory code={code} />

      {/* Image zoom lightbox */}
      {zoomImg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6" onClick={() => setZoomImg(null)}>
          <button onClick={() => setZoomImg(null)} className="absolute right-5 top-4 text-2xl text-white/80 hover:text-white">✕</button>
          <img
            src={`/api/pbs-image/${encodeURIComponent(zoomImg)}`}
            alt={zoomImg}
            className="max-h-[88vh] max-w-[92vw] cursor-zoom-out rounded-lg bg-white object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-black/50 px-3 py-1 font-mono text-xs text-white">{zoomImg}</div>
        </div>
      )}

      {/* ExactOnline item modal (what the app holds in its DB) */}
      {showExact && exact.data && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowExact(false)}>
          <div className="max-h-[85vh] w-full max-w-xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
              <h3 className="text-sm font-semibold text-slate-800">
                ExactOnline item in app DB <span className="ml-1 font-mono text-xs text-slate-500">{exact.data.itemCode}</span>
              </h3>
              <button onClick={() => setShowExact(false)} className="text-slate-400 hover:text-slate-700">✕</button>
            </div>
            <div className="max-h-[72vh] space-y-4 overflow-auto p-5">
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Master item (logistics/Items)</div>
                {exact.data.item ? (
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="ItemCode" value={<span className="font-mono">{exact.data.item.itemCode}</span>} />
                    <Field label="SearchCode (PBS SKU)" value={<span className="font-mono">{exact.data.item.searchCode}</span>} />
                    <Field label="Description" value={exact.data.item.description} />
                    <Field label="Sales price" value={fmtEur(exact.data.item.standardSalesPrice)} />
                    <Field label="Status" value={exact.data.item.active ? "Active" : "Inactive"} />
                    <Field label="End date" value={exact.data.item.endDate ? fmtDateTime(exact.data.item.endDate) : "—"} />
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">Not in the all-items snapshot.</p>
                )}
              </div>
              <div>
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">PBS supplier link (logistics/SupplierItem)</div>
                {exact.data.supplierItem.naMarked && (
                  <div className="mb-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800">
                    ⚠ Supplier item code is prefixed <span className="font-mono font-semibold">N/A</span> — staff marked this discontinued.
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <Field label="SupplierItemCode" value={<span className="font-mono">{exact.data.supplierItem.supplierItemCode}</span>} />
                  <Field label="Linked ItemCode" value={<span className="font-mono">{exact.data.supplierItem.itemCode}</span>} />
                  <Field label="Purchase price" value={fmtEur(exact.data.supplierItem.purchasePrice)} />
                  <Field label="Status" value={exact.data.supplierItem.active ? "Active" : "Inactive"} />
                  <Field label="End date" value={exact.data.supplierItem.endDate ? fmtDateTime(exact.data.supplierItem.endDate) : "—"} />
                  <Field label="Item GUID" value={<span className="break-all font-mono text-xs">{exact.data.supplierItem.itemId}</span>} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
